import React, { useState, useEffect, useCallback } from 'react';
import { leaveAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Modal,
} from '../../UI';
import { CalendarDays, CheckCircle2, XCircle, AlertTriangle, Clock, User, MessageSquare, History, Filter } from 'lucide-react';

const TeamLeavesPage = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [activeView, setActiveView] = useState('pending'); // 'pending' | 'history'
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'approved' | 'rejected' | 'cancelled'
  const [historyLeaves, setHistoryLeaves] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState({ total: 0, pages: 0 });

  const { data: pendingData, loading, refetch } = useApi(
    leaveAPI.getPendingLeaves,
    []
  );

  const { loading: actionLoading, execute: executeAction } = useAsyncAction();

  const pendingLeaves = Array.isArray(pendingData) ? pendingData : (pendingData?.leaves || []);

  const fetchHistory = useCallback(async (page = 1, status = historyFilter) => {
    setHistoryLoading(true);
    try {
      const params = { page, limit: 20 };
      if (status !== 'all') params.status = status;
      const result = await leaveAPI.getTeamLeaveHistory(params);
      const data = result?.data;
      setHistoryLeaves(Array.isArray(data) ? data : (data?.data || data?.leaves || []));
      setHistoryPagination(data?.pagination || { total: 0, pages: 0 });
      setHistoryPage(page);
    } catch (err) {
      setError('Failed to load leave history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilter]);

  useEffect(() => {
    if (activeView === 'history') {
      fetchHistory(1, historyFilter);
    }
  }, [activeView, historyFilter]);

  const handleAction = (leave, type) => {
    setSelectedLeave(leave);
    setActionType(type);
    setComments('');
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    try {
      setError('');
      let result;
      if (actionType === 'approve') {
        result = await executeAction(leaveAPI.approveLeave, selectedLeave._id, comments);
      } else {
        if (!comments.trim()) {
          setError('Please provide a reason for rejection');
          return;
        }
        result = await executeAction(leaveAPI.rejectLeave, selectedLeave._id, comments);
      }

      if (result.success) {
        setSuccess(`Leave ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`);
        setShowActionModal(false);
        refetch();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || `Failed to ${actionType} leave`);
      }
    } catch (err) {
      setError(err.message || 'Action failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getLeaveTypeBadge = (type) => {
    const colors = {
      casual: 'bg-blue-100 text-blue-700',
      sick: 'bg-red-100 text-red-700',
      earned: 'bg-green-100 text-green-700',
      unpaid: 'bg-gray-100 text-gray-700',
      maternity: 'bg-pink-100 text-pink-700',
      paternity: 'bg-indigo-100 text-indigo-700',
      compensatory: 'bg-purple-100 text-purple-700',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
        {type}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3 mr-1" /> },
      approved: { color: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="w-3 h-3 mr-1" /> },
      rejected: { color: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3 mr-1" /> },
      cancelled: { color: 'bg-gray-100 text-gray-600', icon: <XCircle className="w-3 h-3 mr-1" /> },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c.color}`}>
        {c.icon}{status}
      </span>
    );
  };

  const calculateDays = (leave) => {
    if (leave.numberOfDays) return leave.numberOfDays;
    const start = new Date(leave.startDate);
    const end = new Date(leave.endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-emerald-600" />
            Team Leave Requests
          </h2>
          <p className="text-gray-500 mt-1">Manage and review leave requests from your team</p>
        </div>
        {activeView === 'pending' && pendingLeaves.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
              <Clock className="w-4 h-4 mr-1" />
              {pendingLeaves.length} Pending
            </span>
          </div>
        )}
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveView('pending')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeView === 'pending'
              ? 'bg-white text-emerald-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Clock className="w-4 h-4" />
          Pending Actions
          {pendingLeaves.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-amber-500 text-white">
              {pendingLeaves.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveView('history')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeView === 'history'
              ? 'bg-white text-emerald-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <History className="w-4 h-4" />
          Leave History
        </button>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} />}

      {/* Pending View */}
      {activeView === 'pending' && (
        <>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : pendingLeaves.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">All caught up!</p>
              <p className="text-sm text-gray-400 mt-1">No pending leave requests from your team</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingLeaves.map((leave) => {
                const emp = leave.employee || {};
                const days = calculateDays(leave);

                return (
                  <div
                    key={leave._id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                            {emp.name?.charAt(0)?.toUpperCase() || 'E'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{emp.name}</h3>
                            <p className="text-xs text-gray-500">{emp.employeeId} | {emp.designation}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          {getLeaveTypeBadge(leave.leaveType)}
                          <span className="text-sm text-gray-600">
                            {formatDate(leave.startDate)} — {formatDate(leave.endDate)}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            ({days} day{days !== 1 ? 's' : ''})
                          </span>
                          {leave.isHalfDay && <Badge variant="info" size="sm">Half Day</Badge>}
                        </div>
                        {leave.reason && (
                          <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                            <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <p>{leave.reason}</p>
                          </div>
                        )}
                        <p className="text-xs text-gray-400">
                          Applied on {formatDate(leave.createdAt || leave.appliedDate)}
                        </p>
                      </div>
                      <div className="flex sm:flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleAction(leave, 'approve')}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors text-sm font-medium"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleAction(leave, 'reject')}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors text-sm font-medium"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* History View */}
      {activeView === 'history' && (
        <>
          {/* Status Filter */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'approved', label: 'Approved' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'cancelled', label: 'Cancelled' },
              { key: 'pending', label: 'Pending' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setHistoryFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  historyFilter === f.key
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {historyLoading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : historyLeaves.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No leave history found</p>
              <p className="text-sm text-gray-400 mt-1">
                {historyFilter !== 'all' ? `No ${historyFilter} leaves` : 'No leave records yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {historyLeaves.map((leave) => {
                  const emp = leave.employee || {};
                  const days = calculateDays(leave);

                  return (
                    <div
                      key={leave._id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                              {emp.name?.charAt(0)?.toUpperCase() || 'E'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-gray-900">{emp.name}</h3>
                                {getStatusBadge(leave.status)}
                              </div>
                              <p className="text-xs text-gray-500">{emp.employeeId || emp.username} | {emp.department}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 pl-13">
                            {getLeaveTypeBadge(leave.leaveType)}
                            <span className="text-sm text-gray-600">
                              {formatDate(leave.startDate)} — {formatDate(leave.endDate)}
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              ({days} day{days !== 1 ? 's' : ''})
                            </span>
                            {leave.isHalfDay && <Badge variant="info" size="sm">Half Day</Badge>}
                          </div>
                          {leave.reason && (
                            <p className="text-sm text-gray-500 pl-13">
                              <span className="font-medium">Reason:</span> {leave.reason}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 pl-13">
                            <span>Applied: {formatDate(leave.createdAt)}</span>
                            {leave.reviewedBy && (
                              <span>Reviewed by: {leave.reviewedBy.name}</span>
                            )}
                            {leave.reviewedOn && (
                              <span>on {formatDate(leave.reviewedOn)}</span>
                            )}
                          </div>
                          {leave.reviewerComments && (
                            <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg ml-13">
                              <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <p><span className="font-medium">Comments:</span> {leave.reviewerComments}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {historyPagination.pages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <button
                    onClick={() => fetchHistory(historyPage - 1)}
                    disabled={historyPage <= 1}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {historyPage} of {historyPagination.pages} ({historyPagination.total} total)
                  </span>
                  <button
                    onClick={() => fetchHistory(historyPage + 1)}
                    disabled={historyPage >= historyPagination.pages}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Action Modal */}
      {showActionModal && selectedLeave && (
        <Modal
          isOpen={showActionModal}
          onClose={() => setShowActionModal(false)}
          title={actionType === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
        >
          <div className="space-y-4">
            {/* Leave Summary */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-900">{selectedLeave.employee?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CalendarDays className="w-4 h-4 text-gray-400" />
                <span>
                  {formatDate(selectedLeave.startDate)} — {formatDate(selectedLeave.endDate)} 
                  ({calculateDays(selectedLeave)} day{calculateDays(selectedLeave) !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="flex items-center gap-2">
                {getLeaveTypeBadge(selectedLeave.leaveType)}
              </div>
              {selectedLeave.reason && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Reason:</span> {selectedLeave.reason}
                </p>
              )}
            </div>

            {actionType === 'approve' ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  <p className="text-sm text-green-800">This leave request will be approved and the employee will be notified.</p>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-800">This leave request will be rejected. Please provide a reason below.</p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments {actionType === 'reject' ? '(Required)' : '(Optional)'}
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={actionType === 'reject' ? 'Provide reason for rejection...' : 'Add any comments...'}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowActionModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant={actionType === 'approve' ? 'success' : 'danger'}
                onClick={confirmAction}
                loading={actionLoading}
                className="flex-1"
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TeamLeavesPage;
